import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useFormToken } from './useFormToken.js';
import {
  Box,
  Button,
  Label,
  Text,
  Input,
  Icon,
  FormMessage,
  FormGroup,
} from '@adminjs/design-system';

type ComplaintFields = {
  submitterName: string;
  submitterEmail: string;
  submitterPhone: string;
  submitterDepartment: string;
  groupId: string;
  title: string;
  message: string;
};

type ComplaintFormPageProps = {
  onBack: () => void;
};

const ComplaintFormPage: React.FC<ComplaintFormPageProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const { token, tokenError } = useFormToken();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ComplaintFields>({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  useEffect(() => {
    async function fetchGroups() {
      setGroupsLoading(true);
      setGroupsError(null);
      try {
        const res = await fetch('/api/groups');
        if (!res.ok) throw new Error('Failed to fetch groups');
        const data = await res.json();
        setGroups(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setGroupsError(err.message || 'Error loading groups');
      } finally {
        setGroupsLoading(false);
      }
    }
    fetchGroups();
  }, []);

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const onSubmit = async (data: ComplaintFields) => {
    setSubmitError(null);
    setLoading(true);

    try {
      const res = await fetch('/api/complaints', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          _formToken: token,
          'cf-turnstile-response': turnstileToken,
        }),
      });
      if (!res.ok) {
        let result = {};
        try { result = await res.json(); } catch { }
        console.error(result);

        // Handle server-side validation errors
        if ((result as any)?.errors) {
          const serverErrors = (result as any).errors;
          Object.keys(serverErrors).forEach((key) => {
            setError(key as keyof ComplaintFields, {
              type: 'server',
              message: serverErrors[key].message || serverErrors[key],
            });
          });
        }

        setSubmitError((result as any)?.message || 'Failed to submit complaint.');
        setLoading(false);
        return;
      }
      const result = await res.json();
      setSubmittedId(result.id);
    } catch (err: any) {
      setSubmitError(err.message || 'Network error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          borderBottom: '4px solid #dc2626',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Box
          p="xl"
          style={{ maxWidth: '800px', margin: '0 auto' }}
        >
          <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
            <Button
              variant="text"
              onClick={onBack}
              style={{ color: 'rgba(255,255,255,0.8)', padding: '8px 12px' }}
            >
              <Icon icon="ChevronLeft" /> Back
            </Button>
            <Text fontSize="h3" fontWeight="bold" style={{ color: 'white' }}>
              Submit a Complaint
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box p="xl" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Box
          bg="white"
          p="xl"
          style={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}
        >
          {/* Success State */}
          {submittedId && (
            <Box
              p="xl"
              style={{
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                borderRadius: '12px',
                border: '2px solid #a7f3d0',
                textAlign: 'center'
              }}
            >
              <Box
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 4px 15px rgba(5, 150, 105, 0.3)'
                }}
              >
                <Text style={{ fontSize: '32px', color: 'white' }}>✓</Text>
              </Box>
              <Text style={{ color: '#047857', fontWeight: 'bold', fontSize: '16px', marginBottom: '12px' }}>
                Complaint submitted successfully!
              </Text>
              <Text style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
                Your complaint ID is:
              </Text>
              <Box
                mt="xs"
                p="sm"
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  border: '2px solid #059669',
                  display: 'inline-block'
                }}
              >
                <Text fontSize="h3" fontWeight="bold" style={{ color: '#059669' }}>
                  #{submittedId}
                </Text>
              </Box>
              <Text mt="default" fontSize="sm" style={{ color: '#6b7280' }}>
                Please save this ID to check the status of your complaint later.
              </Text>
              <Box mt="xl">
                <Button variant="primary" onClick={onBack}>
                  Back to Home
                </Button>
              </Box>
            </Box>
          )}

          {/* Error State */}
          {(submitError || tokenError || turnstileError) && (
            <Box
              mb="lg"
              p="lg"
              style={{
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                borderRadius: '10px',
                border: '2px solid #fca5a5',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)'
              }}
              role="alert"
            >
              <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
                <Box
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  ⚠
                </Box>
                <Text style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '16px' }}>{submitError || tokenError || turnstileError}</Text>
              </Box>
            </Box>
          )}

          {/* Form */}
          {!submittedId && (
            <>
              <form id="complaint-form" onSubmit={handleSubmit(onSubmit)} aria-live="polite">
                <Box
                  mb="default"
                  pb="default"
                  style={{ borderBottom: '2px solid #e5e7eb' }}
                >
                  <Text fontSize="h4" fontWeight="bold" style={{ color: '#1f2937' }}>Contact Information</Text>
                </Box>

                <FormGroup error={!!errors.submitterName}>
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>First and last name</Label>
                  <Input
                    variant="lg"
                    type="text"
                    {...register('submitterName', {
                      required: 'Name is required',
                    })}
                    placeholder="Your full name"
                    disabled={loading}
                    aria-invalid={!!errors.submitterName}
                    aria-describedby={errors.submitterName ? 'error-name' : undefined}
                    autoComplete="name"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterName && <FormMessage>{errors.submitterName.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.submitterEmail} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Email</Label>
                  <Input
                    variant="lg"
                    type="email"
                    {...register('submitterEmail', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Please enter a valid email address',
                      },
                    })}
                    placeholder="your.email@example.com"
                    disabled={loading}
                    aria-invalid={!!errors.submitterEmail}
                    aria-describedby={errors.submitterEmail ? 'error-email' : undefined}
                    autoComplete="email"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterEmail && <FormMessage>{errors.submitterEmail.message}</FormMessage>}
                </FormGroup>
                <FormGroup error={!!errors.submitterDepartment} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Department</Label>
                  <Input
                    type="text"
                    variant="lg"
                    {...register('submitterDepartment', {
                      required: 'Department is required',
                    })}
                    placeholder="Your department"
                    disabled={loading}
                    aria-invalid={!!errors.submitterDepartment}
                    aria-describedby={errors.submitterDepartment ? 'error-department' : undefined}
                    autoComplete="organization"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterDepartment && <FormMessage>{errors.submitterDepartment.message}</FormMessage>}
                </FormGroup>
                <FormGroup error={!!errors.submitterPhone} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Intercom / Phone #</Label>
                  <Input
                    variant="lg"
                    type="tel"
                    {...register('submitterPhone', {
                      required: 'Phone number is required',
                    })}
                    placeholder="Phone or intercom number"
                    disabled={loading}
                    aria-invalid={!!errors.submitterPhone}
                    aria-describedby={errors.submitterPhone ? 'error-phone' : undefined}
                    autoComplete="tel"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterPhone && <FormMessage>{errors.submitterPhone.message}</FormMessage>}
                </FormGroup>
                <Box
                  mt="xl"
                  mb="default"
                  pb="default"
                  style={{ borderBottom: '2px solid #e5e7eb' }}
                >
                  <Text fontSize="h4" fontWeight="bold" style={{ color: '#1f2937' }}>Complaint Details</Text>
                </Box>

                <FormGroup error={!!errors.groupId}>
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Nature of complaint</Label>
                  {groupsLoading ? (
                    <Text color="grey60" fontSize="sm">Loading groups...</Text>
                  ) : groupsError ? (
                    <Text style={{ color: '#dc2626' }} fontSize="sm">{groupsError}</Text>
                  ) : (
                    <Input as="select"
                      variant="lg"
                      {...register('groupId', {
                        required: 'Please select a complaint type',
                      })}
                      style={{
                        padding: '10px 14px',
                        fontSize: '15px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb'
                      }}
                      disabled={loading}
                      aria-invalid={!!errors.groupId}
                      aria-describedby={errors.groupId ? 'error-group' : undefined}
                    >
                      <option value="">Select type</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </Input>
                  )}
                  {errors.groupId && <FormMessage>{errors.groupId.message}</FormMessage>}
                </FormGroup>
                <FormGroup error={!!errors.title} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Title</Label>
                  <Input
                    variant="lg"
                    type="text"
                    {...register('title', {
                      required: 'Title is required',
                    })}
                    placeholder="Brief summary of your complaint"
                    disabled={loading}
                    aria-invalid={!!errors.title}
                    aria-describedby={errors.title ? 'error-title' : undefined}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.title && <FormMessage>{errors.title.message}</FormMessage>}
                </FormGroup>
                <FormGroup error={!!errors.message} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>
                    Message
                  </Label>
                  <Input
                    variant="lg"
                    as="textarea"
                    {...register('message', {
                      required: 'Message is required',
                      validate: (value) => {
                        const wordCount = countWords(value);
                        if (wordCount > 100) {
                          return 'Message must not exceed 100 words';
                        }
                        return true;
                      },
                    })}
                    placeholder="Describe your issue in detail (max 100 words)..."
                    minRows={8}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'error-message' : undefined}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.message && <FormMessage>{errors.message.message}</FormMessage>}
                </FormGroup>

                {/* Turnstile */}
                <Box mt="lg">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    options={{ theme: 'light', size: 'normal' }}
                    onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(null); }}
                    onError={() => setTurnstileError('Security check failed. Please try again.')}
                    onExpire={() => { setTurnstileToken(null); setTurnstileError(null); }}
                  />
                  {turnstileError && (
                    <Box mt="sm" display="flex" alignItems="center" style={{ gap: '8px' }}>
                      <Text style={{ color: '#dc2626', fontSize: '13px' }}>{turnstileError}</Text>
                      <button
                        type="button"
                        onClick={() => { setTurnstileError(null); turnstileRef.current?.reset(); }}
                        style={{
                          background: 'none',
                          border: '1px solid #dc2626',
                          borderRadius: '4px',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '2px 8px',
                        }}
                      >
                        Retry
                      </button>
                    </Box>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box mt="xl" display="flex" justifyContent="flex-end" style={{ gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => { reset(); setSubmitError(null); }}
                    disabled={loading}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      background: 'white',
                      color: '#374151',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !token || !turnstileToken}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: 'none',
                      background: (loading || !token || !turnstileToken) ? '#93c5fd' : '#1e40af',
                      color: 'white',
                      cursor: (loading || !token || !turnstileToken) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Submitting...' : (!token && !tokenError) ? 'Loading...' : 'Submit Complaint'}
                  </button>
                </Box>
              </form>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ComplaintFormPage;
